import {
  prisma,
  ResourceType,
  ActionType,
  Prisma,
  NetworkingType,
  NetworkingBehavior,
  NetworkingModel,
  OrganizationNetworkingSettings,
  EventNetworkingSettings,
} from "client";

import { getEvent, getEventIdBySlug } from "services/event";
import { resolveAvailabilityEntityId } from "services/availability";
import { AvailabilitiesModel } from "client";

/** Company id when `id` is a team user; null if not on a company team. */
async function resolveNetworkingCompanyId(
  id: string,
  eventSlug: string,
  entityModel: NetworkingModel
): Promise<string | null> {
  if (entityModel === NetworkingModel.USER) return null;
  const availabilityModel =
    entityModel === NetworkingModel.EXHIBITOR
      ? AvailabilitiesModel.EXHIBITOR
      : entityModel === NetworkingModel.SPONSOR
        ? AvailabilitiesModel.SPONSOR
        : AvailabilitiesModel.USER;
  return resolveAvailabilityEntityId(id, eventSlug, availabilityModel);
}

/**
 * Callers pass either a user id or an already-resolved company entity id.
 * Prefer company id for new writes; keep passed id when resolve fails (legacy).
 */
async function resolveNetworkingWriteEntityId(
  id: string,
  eventSlug: string,
  entityModel: NetworkingModel
): Promise<string> {
  if (entityModel === NetworkingModel.USER) return id;
  const companyId = await resolveNetworkingCompanyId(id, eventSlug, entityModel);
  return companyId ?? id;
}

export const createNetworkingSettings = async (
  eventSlug: string,
  userId: string,
  entityModel: NetworkingModel,
  type?: NetworkingType,
  behavior?: NetworkingBehavior
) => {
  try {
    const event = await getEventIdBySlug(eventSlug, { slug: true });
    if (!event) {
      throw new Error(`Event with slug ${eventSlug} not found.`);
    }
    const entityId = await resolveNetworkingWriteEntityId(
      userId,
      eventSlug,
      entityModel
    );
    const networkingSettings = await getUserNetworkingSettingsByEventId(
      event?.slug,
      userId,
      entityModel,
      behavior
    );
    if (networkingSettings) {
      return await updateNetworkingSettings(networkingSettings.id, {
        type,
        behavior,
      });
    }
    return await prisma.networking.create({
      data: {
        entityId,
        entityModel,
        eventId: event?.id,
        type: type ?? NetworkingType.PUBLIC,
        behavior: behavior ?? NetworkingBehavior.ALL,
        createdById: userId,
      },
    });
  } catch (error) {
    console.error("Failed to create networking settings:", error);
    throw error;
  }
};
export const createDefaultNetworkingSettings = async (
  eventId: string,
  userId: string,
  entityModel: NetworkingModel,
  session: { id: string },
  participantGroupId?: string,
  type?: NetworkingType,
  behavior?: NetworkingBehavior
) => {
  try {
    const event = await getEvent(eventId);
    if (!event) {
      throw new Error(`Event with ID ${eventId} not found.`);
    }
    const entityId = await resolveNetworkingWriteEntityId(
      userId,
      event.slug,
      entityModel
    );
    const networkingSettings = await getUserNetworkingSettingsByEventId(
      event.slug,
      userId,
      entityModel,
      behavior
    );
    if (networkingSettings) {
      return await updateNetworkingSettings(networkingSettings.id, {
        type,
        behavior,
      });
    }
    let networkType: NetworkingType = NetworkingType.BY_REQUEST;
    if (participantGroupId) {
      const groupDefaultNetworkSetting =
        await prisma.participantGroup.findUnique({
          where: { id: participantGroupId },
          select: {
            defaultNetworkingSetting: true,
          },
        });
      networkType =
        groupDefaultNetworkSetting?.defaultNetworkingSetting ??
        NetworkingType.BY_REQUEST;
    }
    return await prisma.networking.create({
      data: {
        entityId,
        entityModel,
        eventId: event?.id,
        type: networkType,
        behavior: behavior ?? NetworkingBehavior.ALL,
        createdById: session.id,
      },
    });
  } catch (error) {
    console.error("Failed to create networking settings:", error);
    throw error;
  }
};
export const getUserNetworkingSettingsByEventId = async (
  eventSlug: string,
  /** User id or company exhibitor/sponsor id — callers pass both. */
  entityOrUserId: string,
  entityModel: NetworkingModel,
  behavior?: NetworkingBehavior
) => {
  try {
    const event = await getEventIdBySlug(eventSlug);
    if (!event) {
      throw new Error(`Event with slug ${eventSlug} not found.`);
    }
    const whereBase = {
      eventId: event.id,
      entityModel,
      behavior: behavior ?? NetworkingBehavior.ALL,
    };
    const include = { event: true, user: true, exhibitor: true } as const;

    // Prefer company-keyed row when both legacy (user id) and company rows exist.
    const companyId = await resolveNetworkingCompanyId(
      entityOrUserId,
      eventSlug,
      entityModel
    );
    if (companyId) {
      const companyRow = await prisma.networking.findFirst({
        where: { ...whereBase, entityId: companyId },
        include,
      });
      if (companyRow) return companyRow;
    }

    if (!companyId || companyId !== entityOrUserId) {
      return await prisma.networking.findFirst({
        where: { ...whereBase, entityId: entityOrUserId },
        include,
      });
    }
    return null;
  } catch (error) {
    console.error("Failed to get user networking settings:", error);
    throw error;
  }
};
export const updateNetworkingSettings = async (
  id: string,
  data: {
    type?: NetworkingType;
    behavior?: NetworkingBehavior;
  }
) => {
  return await prisma.networking.update({
    where: { id },
    data,
  });
};

/* **************************************ADMIN SETTINGS ***************************************************** */
// Types
export enum ProfileCreationType {
  REQUIRED = "REQUIRED",
  OPTIONAL = "OPTIONAL",
  DISABLED = "DISABLED",
}

export interface UpdateEventNetworkingSettingsInput {
  enabled?: boolean;
  profileEnabled?: boolean;
  requireSignUp?: boolean;
  supportEmail?: string | null;
}

export interface NetworkingSettingsType {
  enabled: boolean;
  applyToAll: boolean;
  allowedEventIds: string[];
  profileEnabled: boolean;
  profileType: ProfileCreationType;
  autoFillFromProfile: boolean;
  profileAllowedEventIds: string[];
  applyToAllProfile: boolean;
  requireSignUp: boolean;
  allowedOrganizationIds: string[];
  profileAllowedOrganizationIds: string[];
  supportEmail?: string | null;
}

export interface UpdateWhiteLabelNetworkingSettingsInput {
  enabled?: boolean;
  applyToAll?: boolean;
  allowedEventIds?: string[];
  profileEnabled?: boolean;
  profileType?: ProfileCreationType;
  autoFillFromProfile?: boolean;
  profileAllowedEventIds?: string[];
  applyToAllProfile?: boolean;
  requireSignUp?: boolean;
  allowedOrganizationIds?: string[];
  profileAllowedOrganizationIds?: string[];
  supportEmail?: string | null;
}

// WhiteLabel Level Operations
export const updateWhiteLabelNetworkingSettings = async ({
  whiteLabelId,
  settings,
  userId,
}: {
  whiteLabelId: string;
  settings: NetworkingSettingsType;
  userId: string;
}) => {
    const whiteLabelData = await prisma.whiteLabel.findFirst({
    where: {
      id: whiteLabelId,
    },
    include: {
      events: true,
      organizations: true,
    },
  });
  if (!whiteLabelData) {
    throw new Error("WhiteLabel not found");
  }
  const eventsNotInTheList = whiteLabelData?.events.filter(
    (event) => !settings.allowedEventIds.includes(event.id)
  );
  const organizationsNotInTheList = whiteLabelData?.organizations.filter(
    (organization) => !settings.allowedOrganizationIds.includes(organization.id)
  );
  if (eventsNotInTheList.length > 0) {
    await prisma.event.updateMany({
      where: {
        id: { in: eventsNotInTheList.map((event) => event.id) },
      },
      data: {
        networkingSettings: undefined,
      },
    });
  }
  if (organizationsNotInTheList.length > 0) {
    await prisma.organization.updateMany({
      where: {
        id: {
          in: organizationsNotInTheList.map((organization) => organization.id),
        },
      },
      data: {
        networkingSettings: undefined,
      },
    });
  }

  const whiteLabel = await prisma.whiteLabel.update({
    where: { id: whiteLabelId },
    data: {
      networkingSettings: settings,
    },
  });
  const events = await prisma.event.updateMany({
    where: { id: { in: settings.allowedEventIds } },
    data: {
      networkingSettings: {
        requireSignUp: settings.requireSignUp,
        profileEnabled: settings.profileEnabled,
        enabled: settings.enabled,
        profileType: settings.profileType,
        supportEmail: settings.supportEmail ?? null,
      },
    },
  });
  console.log("events", events);
  await prisma.organization.updateMany({
    where: { id: { in: settings.allowedOrganizationIds } },
    data: {
      networkingSettings: {
        requireSignUp: settings.requireSignUp,
        profileEnabled: settings.profileEnabled,
        enabled: settings.enabled,
        profileType: settings.profileType,
        supportEmail: settings.supportEmail ?? null,
      },
    },
  });
  return whiteLabel.networkingSettings;
};

export const getWhiteLabelNetworkingSettings = async (whiteLabelId: string) => {
  const whiteLabel = await prisma.whiteLabel.findUnique({
    where: { id: whiteLabelId },
  });

  if (!whiteLabel) {
    throw new Error("WhiteLabel not found");
  }

  return whiteLabel.networkingSettings;
};

// Organization Level Operations
export const updateOrganizationNetworkingSettings = async ({
  organizationId,
  settings,
  userId,
}: {
  organizationId: string;
  settings: OrganizationNetworkingSettings;
  userId: string;
}) => {
    const organizationData = await prisma.organization.findUnique({
    where: { id: organizationId },
    include: {
      whiteLabel: true,
      events: true,
    },
  });
  if (!organizationData) {
    throw new Error("Organization not found");
  }
  const eventsNotInTheList = organizationData?.events.filter(
    (event) => !settings.allowedEventIds.includes(event.id)
  );

  if (eventsNotInTheList.length > 0) {
    await prisma.event.updateMany({
      where: { id: { in: eventsNotInTheList.map((event) => event.id) } },
      data: { networkingSettings: undefined },
    });
  }

  const organization = await prisma.organization.update({
    where: { id: organizationId },
    data: {
      networkingSettings: {
        enabled: settings.enabled ?? false,
        applyToAll: settings.applyToAll ?? true,
        allowedEventIds: settings.allowedEventIds ?? [],
        profileEnabled: settings.profileEnabled ?? false,
        profileType: settings.profileType ?? ProfileCreationType.OPTIONAL,
        autoFillFromProfile: settings.autoFillFromProfile ?? true,
        profileAllowedEventIds: settings.profileAllowedEventIds ?? [],
        applyToAllProfile: settings.applyToAllProfile ?? true,
        requireSignUp: settings.requireSignUp ?? false,
        supportEmail: settings.supportEmail ?? null,
      },
    },
  });

  settings.allowedEventIds.forEach(async (eventId) => {
    await prisma.event.update({
      where: { id: eventId },
      data: {
        networkingSettings: {
          requireSignUp: settings.requireSignUp,
          profileEnabled: settings.profileEnabled,
          enabled: settings.enabled,
          profileType: settings.profileType,
          supportEmail: settings.supportEmail ?? null,
        },
      },
    });
  });
  return organization.networkingSettings;
};

export const getOrganizationNetworkingSettings = async (
  organizationId: string
) => {
  const organization = await prisma.organization.findUnique({
    where: { id: organizationId },
    include: {
      whiteLabel: true,
    },
  });

  if (!organization) {
    throw new Error("Organization not found");
  }

  return organization.networkingSettings;
};

// Event Level Operations
export const updateEventNetworkingSettings = async ({
  eventId,
  settings,
  userId,
}: {
  eventId: string;
  settings: UpdateEventNetworkingSettingsInput;
  userId: string;
}) => {
    const event = await prisma.event.update({
    where: { id: eventId },
    data: {
      networkingSettings: {
        enabled: settings.enabled ?? false,
        profileEnabled: settings.profileEnabled ?? false,
        requireSignUp: settings.requireSignUp ?? false,
        supportEmail: settings.supportEmail ?? null,
      },
    },
  });

  return event.networkingSettings;
};

export const getEventNetworkingSettingsAdminSide = async ({
  eventId,
  userId,
}: {
  eventId: string;
  userId: string;
}) => {
    const event = await prisma.event.findUnique({
    where: { id: eventId },
    select: { networkingSettings: true },
  });

  return event?.networkingSettings || null;
};

// Validation Methods
export const isNetworkingEnabledForOrganization = async ({
  organizationId,
  whiteLabelId,
}: {
  organizationId: string;
  whiteLabelId: string;
}) => {
  const [whiteLabel, organization] = await Promise.all([
    prisma.whiteLabel.findUnique({
      where: { id: whiteLabelId },
      select: { networkingSettings: true },
    }),
    prisma.organization.findUnique({
      where: { id: organizationId },
      select: { networkingSettings: true },
    }),
  ]);

  // Check WhiteLabel level first
  if (!whiteLabel?.networkingSettings?.enabled) {
    return false;
  }

  // If WhiteLabel applies to all organizations
  if (whiteLabel.networkingSettings.applyToAll) {
    return true;
  }

  // Check if organization is specifically allowed
  if (!whiteLabel.networkingSettings.allowedEventIds.includes(organizationId)) {
    return false;
  }

  // Check organization's own settings
  return organization?.networkingSettings?.enabled ?? false;
};

export const isNetworkingEnabledForEvent = async ({
  eventId,
  organizationId,
}: {
  eventId: string;
  organizationId: string;
}) => {
  const [organization, event] = await Promise.all([
    prisma.organization.findUnique({
      where: { id: organizationId },
      select: {
        networkingSettings: true,
        whiteLabelId: true,
      },
    }),
    prisma.event.findUnique({
      where: { id: eventId },
      select: { networkingSettings: true },
    }),
  ]);

  if (!organization) {
    return false;
  }

  // Check if networking is enabled at organization level
  const isOrgEnabled = await isNetworkingEnabledForOrganization({
    organizationId,
    whiteLabelId: organization.whiteLabelId,
  });

  if (!isOrgEnabled) {
    return false;
  }

  // Check organization's event settings
  if (
    !organization.networkingSettings?.applyToAll &&
    !organization.networkingSettings?.allowedEventIds.includes(eventId)
  ) {
    return false;
  }

  // Finally check event's own setting
  return event?.networkingSettings?.enabled ?? false;
};

export const isProfileRequired = async ({
  whiteLabelId,
  userId,
}: {
  whiteLabelId: string;
  userId: string;
}) => {
    const whiteLabel = await prisma.whiteLabel.findUnique({
    where: { id: whiteLabelId },
    select: { networkingSettings: true },
  });

  return whiteLabel?.networkingSettings?.profileEnabled ?? false;
};

export const isProfileCreationRequired = async ({
  whiteLabelId,
  organizationId,
  eventId,
}: {
  whiteLabelId: string;
  organizationId?: string;
  eventId?: string;
}) => {
  const whiteLabel = await prisma.whiteLabel.findUnique({
    where: { id: whiteLabelId },
    select: { networkingSettings: true },
  });

  // Check WhiteLabel level first
  if (!whiteLabel?.networkingSettings?.profileEnabled) {
    return false;
  }

  // If required at WhiteLabel level, it's required everywhere
  if (
    whiteLabel.networkingSettings.profileType === ProfileCreationType.REQUIRED
  ) {
    return true;
  }

  // If disabled at WhiteLabel level, it's disabled everywhere
  if (
    whiteLabel.networkingSettings.profileType === ProfileCreationType.DISABLED
  ) {
    return false;
  }

  // If optional, check organization settings
  if (organizationId) {
    // Check if organization is allowed to require profiles
    if (
      !whiteLabel.networkingSettings.allowedEventIds?.includes(organizationId)
    ) {
      return false;
    }

    const organization = await prisma.organization.findUnique({
      where: { id: organizationId },
      select: { networkingSettings: true },
    });

    if (!organization?.networkingSettings?.profileEnabled) {
      return false;
    }

    // If event ID is provided, check event-specific settings
    if (eventId) {
      // Check if event is allowed to require profiles
      if (
        !organization.networkingSettings.profileAllowedEventIds?.includes(
          eventId
        )
      ) {
        return false;
      }

      const event = await prisma.event.findUnique({
        where: { id: eventId },
        select: { networkingSettings: true },
      });

      return event?.networkingSettings?.profileEnabled ?? false;
    }

    return true;
  }

  return false;
};

export const shouldShowLoginPopup = async ({
  whiteLabelId,
  userId,
}: {
  whiteLabelId: string;
  userId: string;
}) => {
  const whiteLabel = await prisma.whiteLabel.findUnique({
    where: { id: whiteLabelId },
    select: { networkingSettings: true },
  });

  return whiteLabel?.networkingSettings?.profileEnabled ?? false;
};

export const shouldAutoFillFromProfile = async ({
  whiteLabelId,
  userId,
}: {
  whiteLabelId: string;
  userId: string;
}) => {
  const whiteLabel = await prisma.whiteLabel.findUnique({
    where: { id: whiteLabelId },
    select: { networkingSettings: true },
  });

  return whiteLabel?.networkingSettings?.autoFillFromProfile ?? false;
};

export const getEventNetworkingSettings = async (eventId: string) => {
  if (!eventId) return null;
  const event = await prisma.event.findUnique({
    where: { id: eventId },
    include: {
      organizations: true,
      whiteLabel: true,
    },
  });

  if (!event) {
    return null;
  }
  let settings: EventNetworkingSettings = {
    enabled: false,
    profileEnabled: false,
    requireSignUp: false,
    profileType: ProfileCreationType.OPTIONAL,
    restrictMeetingsToEventDates: true,
    meetingSlotDuration: 30,
    switchToBoothAdmin: false,
    supportEmail: null,
  };
  if (event.networkingSettings) {
    settings = event.networkingSettings;
    return settings;
  }
  if (event.organizations.length > 0) {
    for (const organization of event.organizations) {
      if (organization.networkingSettings) {
        settings = {
          enabled: organization.networkingSettings.enabled,
          profileEnabled: organization.networkingSettings.profileEnabled,
          requireSignUp: organization.networkingSettings.requireSignUp,
          profileType: organization.networkingSettings.profileType,
          restrictMeetingsToEventDates: true,
          meetingSlotDuration: 30,
          switchToBoothAdmin: false,
          supportEmail: organization.networkingSettings.supportEmail ?? null,
        };
        return settings;
      }
    }
  }
  const organizationIds = event.organizations.map(
    (organization) => organization.id
  );
  if (event.whiteLabel) {
    if (
      event.whiteLabel.networkingSettings &&
      (event.whiteLabel.networkingSettings?.allowedOrganizationIds?.some((id) =>
        organizationIds.includes(id)
      ) ||
        event.whiteLabel.networkingSettings?.allowedEventIds.includes(
          eventId
        ) ||
        event.whiteLabel.networkingSettings?.applyToAll)
    ) {
      settings = {
        enabled: event.whiteLabel.networkingSettings.enabled,
        profileEnabled: event.whiteLabel.networkingSettings.profileEnabled,
        requireSignUp: event.whiteLabel.networkingSettings.requireSignUp,
        profileType: event.whiteLabel.networkingSettings.profileType,
        restrictMeetingsToEventDates: true,
        meetingSlotDuration: 30,
        switchToBoothAdmin: false,
        supportEmail: event.whiteLabel.networkingSettings.supportEmail ?? null,
      };
    }

    return settings;
  }
  return settings;
};
